import React, { useState } from 'react'
import { X, UserPlus, Lock, Shield, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { doc, addDoc, collection, setDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase-config'
import bcrypt from 'bcryptjs'
import { Button } from '@/components/ui/Button'
import { SECURITY_QUESTIONS, type SecurityQuestion, type RecoveryAttempts } from '../../types/auth'
import type { ChildProfile } from '../../types/auth'
import { checkUsernameExists } from '../../utils/usernameValidation'

interface ChildCreationModalProps {
  isOpen: boolean
  onClose: () => void
  parentId: string
  onSuccess: (childId: string) => void
}

export const ChildCreationModal: React.FC<ChildCreationModalProps> = ({
  isOpen,
  onClose,
  parentId,
  onSuccess
}) => {
  const [step, setStep] = useState(1) // 1: Username+Age, 2: PIN+Security
  const [formData, setFormData] = useState({
    username: '',
    age: '',
    pin: '',
    confirmPin: ''
  })
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [showSecurityQuestions, setShowSecurityQuestions] = useState(false)
  const [showPins, setShowPins] = useState({ pin: false, confirmPin: false })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Reset modal state when it opens or closes
  React.useEffect(() => {
    if (isOpen) {
      // Reset everything when modal opens
      setStep(1)
      setFormData({
        username: '',
        age: '',
        pin: '',
        confirmPin: ''
      })
      setSelectedQuestions([])
      setAnswers({})
      setShowSecurityQuestions(false)
      setShowPins({ pin: false, confirmPin: false })
      setError('')
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleCreate = async () => {
    if (!formData.username.trim()) {
      setError('Please enter a username')
      return
    }
    if (!formData.age) {
      setError('Please select age')
      return
    }
    if (formData.pin.length !== 4) {
      setError('PIN must be 4 digits')
      return
    }
    if (formData.pin !== formData.confirmPin) {
      setError('PINs must match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const usernameTaken = await checkUsernameExists(formData.username)
      if (usernameTaken) {
        setError('Oops, that username already exists. Choose another one')
        setLoading(false)
        return
      }

      const hashedPin = await bcrypt.hash(formData.pin, 10)
      
      let securityQuestions: SecurityQuestion[] = []
      if (selectedQuestions.length >= 2) {
        securityQuestions = await Promise.all(
          selectedQuestions.map(async (questionId) => {
            const question = SECURITY_QUESTIONS.find(q => q.id === questionId)!
            const answerHash = await bcrypt.hash(answers[questionId].toLowerCase().trim(), 10)
            return {
              id: questionId,
              question: question.question,
              answerHash
            }
          })
        )
      }
      
      const initialRecovery: RecoveryAttempts = {
        sessionAttempts: 0,
        lastSessionAttempt: new Date(),
        totalSessions: 0,
        isInCooldown: false,
        requiresParentReset: false
      }
      
      const childProfile: ChildProfile = {
        childId: 'temp_id',
        parentId,
        username: formData.username.trim().toLowerCase(),
        age: parseInt(formData.age),
        pinHash: hashedPin,
        createdAt: new Date(),
        lastActive: new Date(),
        securityQuestions,
        recoveryAttempts: initialRecovery,
        preferences: {
          fontSize: 'default',
          highContrast: false,
          reducedMotion: false,
          audioSupport: true
        },
        metadata: {
          totalSessions: 0,
          lastActiveDate: null,
          currentLevel: 'New Explorer',
          allowWeeklyReport: true
        }
      }
      
      const docRef = await addDoc(collection(db, 'children'), childProfile)
      await setDoc(doc(db, 'children', docRef.id), { childId: docRef.id }, { merge: true })
      
      onSuccess(docRef.id)
      
    } catch (err: any) {
      setError(err.message || 'Failed to create child profile')
    } finally {
      setLoading(false)
    }
  }

  const handleQuestionToggle = (questionId: string) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(prev => prev.filter(id => id !== questionId))
      const newAnswers = { ...answers }
      delete newAnswers[questionId]
      setAnswers(newAnswers)
    } else if (selectedQuestions.length < 3) {
      setSelectedQuestions(prev => [...prev, questionId])
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Add a Child
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 1 ? (
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-3 border-2 rounded-lg"
                    placeholder="Choose a username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Age</label>
                  <select
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="w-full px-4 py-3 border-2 rounded-lg"
                  >
                    <option value="">Select age</option>
                    {[8,9,10,11,12,13,14,15,16].map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
              </div>
              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
              <div className="flex gap-3 mt-6">
                <Button onClick={onClose} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={() => setStep(2)} className="flex-1 bg-blue-600">Next</Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">PIN (4 digits)</label>
                    <div className="relative">
                      <input
                        type={showPins.pin ? "text" : "password"}
                        autoComplete="new-password"
                        data-lpignore="true"
                        value={formData.pin}
                        onChange={(e) => /^\d{0,4}$/.test(e.target.value) && setFormData({ ...formData, pin: e.target.value })}
                        className="w-full px-4 py-3 pr-12 border-2 rounded-lg text-center font-mono text-xl"
                        placeholder="••••"
                        maxLength={4}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPins({ ...showPins, pin: !showPins.pin })}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showPins.pin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm PIN</label>
                    <div className="relative">
                      <input
                        type={showPins.confirmPin ? "text" : "password"}
                        autoComplete="new-password"
                        data-lpignore="true"
                        value={formData.confirmPin}
                        onChange={(e) => /^\d{0,4}$/.test(e.target.value) && setFormData({ ...formData, confirmPin: e.target.value })}
                        className="w-full px-4 py-3 pr-12 border-2 rounded-lg text-center font-mono text-xl"
                        placeholder="••••"
                        maxLength={4}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPins({ ...showPins, confirmPin: !showPins.confirmPin })}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showPins.confirmPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {formData.pin.length === 4 && formData.confirmPin.length === 4 && formData.pin === formData.confirmPin && !showSecurityQuestions && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Shield className="w-6 h-6 text-yellow-600" />
                      <h4 className="font-semibold">Help Me Remember My PIN</h4>
                    </div>
                    <p className="text-sm mb-3">Want to set up questions to help remember the PIN?</p>
                    <div className="flex gap-3">
                      <Button onClick={() => setShowSecurityQuestions(true)} className="bg-yellow-600">
                        Yes, Add Questions
                      </Button>
                      <Button onClick={() => {}} variant="outline">Skip for Now</Button>
                    </div>
                  </div>
                )}

                {showSecurityQuestions && (
                  <div className="border-2 border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Choose 2-3 Questions
                    </h4>
                    <div className="space-y-3">
                      {SECURITY_QUESTIONS.map(q => {
                        const isSelected = selectedQuestions.includes(q.id)
                        const canSelect = selectedQuestions.length < 3 || isSelected
                        return (
                          <div key={q.id}>
                            <div
                              onClick={() => canSelect && handleQuestionToggle(q.id)}
                              className={`p-3 border-2 rounded-lg cursor-pointer ${isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'} ${!canSelect && 'opacity-50 cursor-not-allowed'}`}
                            >
                              <div className="flex items-center gap-2">
                                {isSelected && <CheckCircle className="w-5 h-5 text-yellow-600" />}
                                <span className="text-sm">{q.question}</span>
                              </div>
                            </div>
                            {isSelected && (
                              <input
                                type="text"
                                value={answers[q.id] || ''}
                                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                                placeholder="Your answer"
                                className="w-full mt-2 px-3 py-2 border-2 rounded-lg text-sm"
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
              <div className="flex gap-3 mt-6">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={handleCreate} disabled={loading} className="flex-1 bg-green-600">
                  {loading ? 'Creating...' : 'Create Child'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}